import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { playbackAssetGrantSchema } from '../types/api/macroExecution';
import { macroTriggerSchema } from '../types/api/macroTree';
import { sha256HexSchema } from '../types/api/macroPackage/canonicalJson';
import { macroPackageServerErrorSchema } from '../types/api/macroPackage/restApi';
import { rawPackageUploadGrantTokenSchema } from '../types/api/macroPackage/uploadAuthorization';
import { connectedHostSchema } from '../types/api/macroEditor/hostSelection';
import { macroWorkspaceSchema, recordingPinSchema } from '../types/api/macroPackage/authoringPrimitives';
import { macroPackageErrorCodeSchema, macroPackageRunCommandSchema, macroPackageRunReplySchema,
  type MacroPackageRunCommand } from '../types/api/macroPackage/packageRun';
import { lockedMacroValidationRequestSchema } from '../types/api/macroPackage/lockedValidation';

/** Host ↔ realtime-api: compiled package delivery. */
export enum MacroPackageMessageType {
  REQUEST = 'MACROPACKAGE_REQUEST',
  RESPONSE = 'MACROPACKAGE_RESPONSE',
  VERIFY_REQUEST = 'MACROPACKAGE_VERIFY_REQUEST',
  VERIFY_RESPONSE = 'MACROPACKAGE_VERIFY_RESPONSE',
  LOCKED_VALIDATION_COMMAND = 'MACROPACKAGE_LOCKED_VALIDATION_COMMAND',
  LOCKED_VALIDATION_REPLY = 'MACROPACKAGE_LOCKED_VALIDATION_REPLY',
}
/** Host ↔ realtime-api: raw package upload through presigned URLs. */
export enum RawPackageMessageType {
  UPLOAD_BEGIN = 'RAWPACKAGE_UPLOAD_BEGIN',
  UPLOAD_GRANTED = 'RAWPACKAGE_UPLOAD_GRANTED',
  UPLOAD_COMPLETE = 'RAWPACKAGE_UPLOAD_COMPLETE',
  INGESTED = 'RAWPACKAGE_INGESTED',
  UPLOAD_PROGRESS = 'RAWPACKAGE_UPLOAD_PROGRESS',
  TRANSFER_REQUEST = 'RAWPACKAGE_TRANSFER_REQUEST',
}

const requestId = z.object({ requestId: z.string().uuid() });
const campaignId = z.string().min(1).max(128);
const fileName = z.string().min(1).max(256);

/** A server-issued owner lease, not authorization inferred from the compiler's campaignId namespace. */
export const rawPackageTransferRequestSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(RawPackageMessageType.TRANSFER_REQUEST), transferId: z.string().uuid(),
  sessionId: z.string().min(1).max(128), workspace: macroWorkspaceSchema, pin: recordingPinSchema,
  uploadGrantToken: rawPackageUploadGrantTokenSchema,
}).strict();
export type RawPackageTransferRequest = z.infer<typeof rawPackageTransferRequestSchema>;

/** `packageSha256` is required for (and only for) preview packages. */
export const macroPackageRequestSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(MacroPackageMessageType.REQUEST), campaignId, trigger: macroTriggerSchema,
  variant: z.enum(['authoritative', 'preview']), packageSha256: sha256HexSchema.optional(),
}).strict().refine(message => (message.variant === 'preview') === (message.packageSha256 !== undefined),
  'packageSha256 iff preview');
export const macroPackageSuccessResponseSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(MacroPackageMessageType.RESPONSE), ok: z.literal(true), packageKey: z.string().min(1).max(512),
  packageSha256: sha256HexSchema, grant: playbackAssetGrantSchema,
}).strict();
export const macroPackageErrorResponseSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(MacroPackageMessageType.RESPONSE), ok: z.literal(false), error: macroPackageServerErrorSchema,
}).strict();
export const macroPackageResponseSchema = z.union([macroPackageSuccessResponseSchema, macroPackageErrorResponseSchema]);

/** Canonical verification only: this carrier cannot authorize Start or input-producing commands. */
export const macroPackageVerificationRequestSchema = baseMessageSchema.extend({
  type: z.literal(MacroPackageMessageType.VERIFY_REQUEST), selectedHost: connectedHostSchema,
  command: z.lazy(() => macroPackageRunCommandSchema).refine(
    (command): command is Extract<MacroPackageRunCommand, { command?: 'capabilities' | 'validate' }> =>
      command.command === 'capabilities' || command.command === 'validate', 'Non-executing verification commands only'),
}).strict();

/** Host/server transport failures for structural verification. These never originate from Playjector. */
export const MACRO_PACKAGE_VERIFICATION_TRANSPORT_ERRORS = [
  'native_ipc_disconnected', 'native_capabilities_timeout', 'native_validation_timeout',
  'native_reply_timeout', 'native_send_failed', 'verification_response_timeout',
  'verification_socket_disconnected', 'verification_send_failed',
] as const;
export const macroPackageVerificationTransportErrorSchema = z.enum(MACRO_PACKAGE_VERIFICATION_TRANSPORT_ERRORS);
export const macroPackageVerificationErrorCodeSchema = z.union([
  macroPackageErrorCodeSchema, macroPackageVerificationTransportErrorSchema,
]);
const macroPackageVerificationTransportFailureSchema = macroPackageRunReplySchema.innerType().extend({
  outcome: z.literal('failed'), inputsSent: z.literal(false),
  errorCode: macroPackageVerificationTransportErrorSchema,
}).strict();
/** Native replies stay frozen; only this verification carrier may report bounded Host/server transport failures. */
export const macroPackageHostReplySchema = z.union([
  macroPackageRunReplySchema, macroPackageVerificationTransportFailureSchema,
]);
export type MacroPackageHostReply = z.infer<typeof macroPackageHostReplySchema>;
export const macroPackageVerificationReplySchema = macroPackageHostReplySchema.refine(reply => !reply.inputsSent
  && ['capabilities', 'validated', 'failed'].includes(reply.outcome), 'Non-executing verification replies only');
export type MacroPackageVerificationReply = z.infer<typeof macroPackageVerificationReplySchema>;
export const macroPackageVerificationResponseSchema = baseMessageSchema.extend({
  type: z.literal(MacroPackageMessageType.VERIFY_RESPONSE), selectedHost: connectedHostSchema,
  reply: z.lazy(() => macroPackageVerificationReplySchema),
}).strict();
export type MacroPackageVerificationRequest = z.infer<typeof macroPackageVerificationRequestSchema>;
export type MacroPackageVerificationResponse = z.infer<typeof macroPackageVerificationResponseSchema>;

/** Separate server-owned optional test carrier. Structural VERIFY remains incapable of starting inputs. */
const validationCarrier = baseMessageSchema.extend({ selectedHost: connectedHostSchema,
  validationId: z.string().uuid(), validation: lockedMacroValidationRequestSchema });
export const macroPackageLockedValidationCommandSchema = validationCarrier.extend({
  type: z.literal(MacroPackageMessageType.LOCKED_VALIDATION_COMMAND),
  command: z.lazy(() => macroPackageRunCommandSchema).refine(
    (command): command is Extract<MacroPackageRunCommand, { command?: 'start' | 'cancel' | 'status' }> =>
      ['start', 'cancel', 'status'].includes(command.command), 'Exact owned test commands only'),
}).strict().superRefine((carrier, context) => {
  const command = carrier.command;
  const targetChanged = carrier.selectedHost.machineId !== carrier.validation.selectedHost.hostId;
  const startChanged = command.command === 'start' && (command.packageSha256 !== carrier.validation.packageSha256
    || command.mode !== (carrier.validation.mode === 'observe' ? 'local-shadow' : 'local-calibrated')
    || command.package.source !== 'grant' || command.captureTarget !== 'selected'
    || command.liveDetection !== undefined || command.previewSettings !== undefined);
  if (targetChanged || startChanged || command.runId !== carrier.validationId) context.addIssue({
    code: z.ZodIssueCode.custom, message: 'Validation command must preserve its exact Host, owned run, package and explicit mode' });
});
export const macroPackageLockedValidationReplySchema = validationCarrier.extend({
  type: z.literal(MacroPackageMessageType.LOCKED_VALIDATION_REPLY), reply: z.lazy(() => macroPackageHostReplySchema),
}).strict().refine(carrier => carrier.reply.runId === carrier.validationId
  && carrier.selectedHost.machineId === carrier.validation.selectedHost.hostId, 'Reply must belong to the exact owned validation');
export type MacroPackageLockedValidationCommand = z.infer<typeof macroPackageLockedValidationCommandSchema>;
export type MacroPackageLockedValidationReply = z.infer<typeof macroPackageLockedValidationReplySchema>;

/** Bounds one control message, not the number of frames in a recording. Hosts request inventory in batches. */
export const RAW_PACKAGE_UPLOAD_MAX_FILES = 4_096;
export const rawPackageUploadBeginSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(RawPackageMessageType.UPLOAD_BEGIN), campaignId, packageId: z.string().uuid(),
  uploadGrantToken: rawPackageUploadGrantTokenSchema.optional(),
  transferId: z.string().uuid().optional(), pin: recordingPinSchema.optional(),
  files: z.array(z.object({ file: fileName, bytes: z.number().int().nonnegative(), sha256: sha256HexSchema }).strict())
    .min(1).max(RAW_PACKAGE_UPLOAD_MAX_FILES),
}).strict().refine(message => message.transferId === undefined ? message.pin === undefined
  : message.uploadGrantToken !== undefined && message.pin?.packageId === message.packageId,
  'Owner transfer requires its exact pin and grant; campaign upload cannot carry a transfer pin');
export const rawPackageUploadGrantedSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(RawPackageMessageType.UPLOAD_GRANTED),
  uploads: z.array(z.object({ file: fileName, url: z.string().url().max(4096) }).strict()).max(RAW_PACKAGE_UPLOAD_MAX_FILES),
}).strict();
export const rawPackageUploadCompleteSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(RawPackageMessageType.UPLOAD_COMPLETE), campaignId, packageId: z.string().uuid(),
  uploadGrantToken: rawPackageUploadGrantTokenSchema.optional(),
  transferId: z.string().uuid().optional(), pin: recordingPinSchema.optional(),
  manifestSha256: sha256HexSchema,
}).strict().refine(message => message.transferId === undefined ? message.pin === undefined
  : message.uploadGrantToken !== undefined && message.pin?.packageId === message.packageId
    && message.pin.manifestSha256 === message.manifestSha256,
  'Owner transfer completion must match its exact manifest pin and grant');
export const rawPackageIngestedSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(RawPackageMessageType.INGESTED), ok: z.boolean(), error: macroPackageServerErrorSchema.optional(),
  campaignId: campaignId.optional(), packageId: z.string().uuid().optional(), manifestSha256: sha256HexSchema.optional(),
}).strict().refine(message => message.ok === (message.error === undefined), 'error iff not ok');
/** A checked operation is still running. This renews transport inactivity, never asserts content validity. */
export const rawPackageUploadProgressSchema = baseMessageSchema.merge(requestId).extend({
  type: z.literal(RawPackageMessageType.UPLOAD_PROGRESS), campaignId, packageId: z.string().uuid(),
  manifestSha256: sha256HexSchema, stage: z.enum(['inventory', 'verifying']),
}).strict();

export const macroPackageMessageSchema = z.union([
  macroPackageRequestSchema, macroPackageSuccessResponseSchema, macroPackageErrorResponseSchema,
  macroPackageVerificationRequestSchema, macroPackageVerificationResponseSchema,
  macroPackageLockedValidationCommandSchema, macroPackageLockedValidationReplySchema,
  rawPackageUploadBeginSchema, rawPackageUploadGrantedSchema, rawPackageUploadCompleteSchema, rawPackageIngestedSchema,
  rawPackageTransferRequestSchema,
  rawPackageUploadProgressSchema,
]);
export type MacroPackageRequest = z.infer<typeof macroPackageRequestSchema>;
export type MacroPackageResponse = z.infer<typeof macroPackageResponseSchema>;
export type RawPackageUploadBegin = z.infer<typeof rawPackageUploadBeginSchema>;
export type RawPackageUploadGranted = z.infer<typeof rawPackageUploadGrantedSchema>;
export type RawPackageUploadComplete = z.infer<typeof rawPackageUploadCompleteSchema>;
export type RawPackageIngested = z.infer<typeof rawPackageIngestedSchema>;
export type RawPackageUploadProgress = z.infer<typeof rawPackageUploadProgressSchema>;
export type MacroPackageMessage = z.infer<typeof macroPackageMessageSchema>;

type Payload<T> = T extends unknown ? Omit<T, 'id' | 'timestamp'> : never;
export function generateMacroPackageMessage(payload: Payload<MacroPackageMessage>): MacroPackageMessage {
  return macroPackageMessageSchema.parse({ id: createMessageId(), timestamp: Date.now(), ...payload });
}
